import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import styles from './ReportGenerationV2.module.less';
import type { ReportGenerationParams } from '../shared/types';
import ReportFilters from './ReportFilters';
import { SidebarLayout } from '../../../layouts';
import ActionButtons from './ActionButtons';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from './shared/types';
import Sidebar from './Sidebar';
import Preview from './Preview';
import useReportContainer from '../ReportGeneration/context/useReportContainer';
import {
  defaultIncludeOptions,
  ntiaflatIncludeValues,
  ntiaLicenseValues,
  ntiaMetadataCheckboxValues,
} from './shared/constants';
import { useRevision } from '../../../../hooks/api';

type Props = {
  params: ReportGenerationParams;
};

const ReportGenerationV2: VoidFunctionComponent<Props> = (props: Props) => {
  const { dependencyInfoOptions } = useReportContainer();
  const [format, setFormat] = useState<FormatName | null>(null);
  const [includeOptions, setIncludeOptions] = useState<IncludeFilterOption[]>([
    ...defaultIncludeOptions,
    // Also enable the NTIA toggle by default
    ...ntiaflatIncludeValues,
    ...ntiaLicenseValues,
  ]);
  const [dependencyMetadataOptions, setDependencyMetadataOptions] = useState<DependencyMetadataFilterOption[]>([
    ...dependencyInfoOptions,
    // Also enable the NTIA toggle by default
    ...ntiaMetadataCheckboxValues,
  ]);
  const [isDirty, setIsDirty] = useState(false);
  const resetFormat = () => {
    setIsDirty(false);
    setFormat(null);
  };
  const isVulnerability = props.params.report_type === 'vulnerability';

  const isProjectReport = !!props.params.project_id && !!props.params.revision_id;
  const isLicensingReport = props.params.report_type === 'licensing';
  const projectRevisionLocator = `${props.params.project_id}$${props.params.revision_id}`;

  // Fetch the current revision to determine if project reports should be downloaded or emailed.
  const revisionResponse = useRevision(projectRevisionLocator, isProjectReport && isLicensingReport);

  return (
    <div className={styles.reportGenerationV2Container}>
      <SidebarLayout>
        <SidebarLayout.Main>
          <div className={styles.reportGenerationV2}>
            <Sidebar params={props.params} resetFormat={resetFormat} />
            <Preview
              params={props.params}
              format={format}
              includeOptions={includeOptions}
              dependencyMetadataOptions={dependencyMetadataOptions}
            />
          </div>
        </SidebarLayout.Main>
        {!isVulnerability && (
          <SidebarLayout.Sidebar>
            <div className={styles.filterSidebar}>
              <ActionButtons
                isDisabled={isDirty}
                reportType={props.params.report_type}
                format={format}
                includeOptions={includeOptions}
                dependencyMetadataOptions={dependencyMetadataOptions}
                revision={revisionResponse.data}
              />
              <ReportFilters
                isApplyButtonDisabled={!isDirty}
                setIsDirty={setIsDirty}
                includeOptions={includeOptions}
                dependencyMetadataOptions={dependencyMetadataOptions}
                reportType={props.params.report_type}
                format={format}
                setFormat={setFormat}
                setIncludeOptions={setIncludeOptions}
                setDependencyMetadataOptions={setDependencyMetadataOptions}
              />
            </div>
          </SidebarLayout.Sidebar>
        )}
      </SidebarLayout>
    </div>
  );
};

export default ReportGenerationV2;
